import { calculateElrGroupsForVideo } from "components/util/Geometry";
import { getRoutePosAtTimestamp } from "components/util/GeometryUtilsV2";
import LoadingOverlay from "components/util/LoadingOverlay";
import { binarySearch } from "components/util/PlaylistUtils";
import * as echarts from "echarts";
import { debounce, get } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { useDispatch, useSelector } from "react-redux";
import { routeSelected, websocketMessage } from "redux/actions";
import { routeGeomSelector, sourceOffsetSelector } from "redux/selectors";
import { boundedRange, boundedValue, useMouseMovement, useOnClick } from "../ChartV2/utils";

const MIN_ZOOM = 30 * 1000; // 30 seconds
const MAX_ZOOM = 3 * 60 * 1000; // 3 minutes

const currentTimestampSelector = (state) => get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "timestamp"], 0);
const isPlayingSelector = (state) => get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "playerState"], "paused") === "playing";
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const sourceIndexSelector = (state) => get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "sourceIndex"], 0);
const playlistVideoSelector = (state) =>
    get(state.playlist.data.video, [get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "sourceIndex"], 0)], []);
const userConfigSelector = (state) => state.userDetails.userConfig;
const sessionIdSelector = (state) => state.playlist.data.routeID;
const sessionKeySelector = (state) => get(state.sessions, [state.playlist.data.routeID, "uuid"]);

const Pantovision = () => {
    const dispatch = useDispatch();
    const currentTimestamp = useSelector(currentTimestampSelector);
    const isPlaying = useSelector(isPlayingSelector);
    const routeGeometry = useSelector(routeGeomSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const playlistVideo = useSelector(playlistVideoSelector);
    const userConfig = useSelector(userConfigSelector);
    const sourceOffset = useSelector(sourceOffsetSelector);
    const sourceIndex = useSelector(sourceIndexSelector);
    const sessionId = useSelector(sessionIdSelector);
    const sessionKey = useSelector(sessionKeySelector);

    const chartRef = useRef(null);
    const maxBoundsRef = useRef([null, null]);

    const [initialised, setInitialised] = useState(false);
    const [hasTraceData, setHasTraceData] = useState(false);
    const [data, setData] = useState(null);
    const [bounds, setBounds] = useState(null);
    const [chart, setChart] = useState(null);
    const [selectedTimestamp, setSelectedTimestamp] = useState(0);

    const [zoomBounds, setZoomBounds] = useState([null, null]);

    useEffect(() => {
        if (sessionId && sessionKey) {
            const url = `https://panto.aivr.video/analysis/${sessionKey}/pantovision_analysis_results_${sessionId}_s${sourceIndex}.json`;

            fetch(url, {
                mode: "cors",
                headers: { "Content-Type": "application/json" },
            })
                .then((response) => {
                    if (!response.ok) {
                        return Promise.reject(response);
                    } else {
                        setHasTraceData(true);
                        return response.json();
                    }
                })
                .then((data) => {
                    setData(data);
                })
                .catch((error) => {
                    setHasTraceData(false);
                    console.log("DEBUG JB:", { error });
                })
                .finally(() => {
                    setInitialised(true);
                });
        }
    }, [sessionId, sessionKey, sourceIndex]);

    useEffect(() => {
        dispatch(routeSelected(null));
        setInitialised(false);
        setHasTraceData(false);
        setData(null);
    }, [dispatch, sessionId]);

    useEffect(() => {
        if (sessionId && currentTimestamp > 0) {
            dispatch(routeSelected(sessionId, 0));
        }
    }, [dispatch, sessionId, currentTimestamp]);

    const routeIndexes = useMemo(() => {
        const routePositionData = get(routeGeometry, ["distance_to_route_position"], []);

        return routePositionData.length > 0 ? calculateElrGroupsForVideo(playlistVideo, routeGeometry, routeSystemID) : null;
    }, [playlistVideo, routeGeometry, routeSystemID]);

    const calculatedPositionString = useCallback(
        (timestamp) => {
            if (timestamp) {
                const calculatedELR = getRoutePosAtTimestamp(timestamp, routeGeometry, routeSystemID);
                if (calculatedELR) {
                    return calculatedELR.to_string(userConfig.elr_units, sourceOffset);
                }
            }
        },
        [routeGeometry, routeSystemID, userConfig.elr_units, sourceOffset],
    );

    useEffect(() => {
        if (bounds && chartRef.current) {
            const chart = echarts.init(chartRef.current, "dark", {
                renderer: "canvas",
                animation: false,
            });
            setChart(chart);
            return () => {
                chart.dispose();
            };
        }
    }, [bounds]);

    useEffect(() => {
        if (currentTimestamp) {
            setSelectedTimestamp(currentTimestamp);
        }
    }, [currentTimestamp]);

    const viewBounds = useCallback(
        (bounds) => {
            if (!chart) return;

            const boundedHighlight = boundedRange(maxBoundsRef.current[0], bounds[0], bounds[1], maxBoundsRef.current[1]);
            chart.setOption({
                xAxis: (chart.getOption().xAxis ?? [])
                    .filter((axis) => axis.id !== "overview_x")
                    .map((axis) => ({
                        id: axis.id,
                        min: boundedHighlight[0],
                        max: boundedHighlight[1],
                    })),
                series: [
                    {
                        id: "current_view_series",
                        data: [boundedHighlight],
                    },
                ],
            });
        },
        [chart],
    );

    const updateCurrentPosition = useCallback(
        (timestamp, zoomBounds, isPlaying = false) => {
            if (!chart) return;

            const currentOption = chart.getOption();
            if (!currentOption) return;

            if (isPlaying) {
                const [min, max] = zoomBounds;
                const [dataMin, dataMax] = maxBoundsRef.current;

                const isVisible = timestamp >= min && timestamp <= max;
                if (isPlaying && !isVisible && timestamp >= dataMin && timestamp <= dataMax) {
                    const range = max - min;
                    let newStart = timestamp - range / 2;
                    let newEnd = timestamp + range / 2;

                    if (newStart < dataMin) {
                        newStart = dataMin;
                        newEnd = dataMin + range;
                    } else if (newEnd > dataMax) {
                        newEnd = dataMax;
                        newStart = dataMax - range;
                    }

                    setZoomBounds([newStart, newEnd]);
                    viewBounds([newStart, newEnd]);
                }
            }

            chart.setOption({
                series: [
                    {
                        id: "selected_timestamp_position_series",
                        data: [[timestamp]],
                    },
                    {
                        id: "selected_timestamp_overview_series",
                        data: [[timestamp]],
                    },
                ],
            });
        },
        [chart, viewBounds],
    );

    useEffect(() => {
        updateCurrentPosition(selectedTimestamp, zoomBounds, isPlaying);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimestamp]);

    const onResize = debounce(({ bounds }) => {
        setBounds(bounds);
        if (chart) {
            chart.resize();
        }
    }, 1000);

    useEffect(() => {
        if (data) {
            const minTimestamp = data.analysis_results[0].ts;
            const maxTimestamp = data.analysis_results[data.analysis_results.length - 1].ts;
            const maxZoomIndex = binarySearch(minTimestamp + MAX_ZOOM, data.analysis_results, (geom) => geom.ts);
            const maxZoomTimestamp = data.analysis_results[maxZoomIndex].ts;

            setZoomBounds([minTimestamp, maxZoomTimestamp]);
            maxBoundsRef.current = [minTimestamp, maxTimestamp];
        }
    }, [data]);

    const chartData = useMemo(() => {
        if (!data) return;

        const result = {
            dataPoints: [],
            height: [],
            smoothed: [],
            acceleration: [],
            contact: [],
            unclassified: [],
            catenary: [],
            poleEventsBySide: [],
            poleEvents: [],
        };

        const range = (zoomBounds[1] - zoomBounds[0]) / 2;
        const startIndex = binarySearch(zoomBounds[0] - range, data.analysis_results, (geom) => geom.ts);
        const endIndex = binarySearch(zoomBounds[1] + range, data.analysis_results, (geom) => geom.ts);

        for (let i = startIndex; i <= endIndex; i++) {
            const d = data.analysis_results[i];

            result.dataPoints.push([d.ts, null]);
            result.height.push([d.ts, d.pan_elevation_mm === null ? null : Number(d.pan_elevation_mm.toFixed(2))]);
            result.smoothed.push([d.ts, d.pan_elevation_mm_smoothed]);
            result.acceleration.push([d.ts, d.acceleration]);

            if (d.pole_events !== undefined) {
                result.poleEvents.push([d.ts, -12000], [d.ts, 12000]);

                d.pole_events.forEach((pole) => {
                    result.poleEventsBySide.push([d.ts, pole === "left" ? -550 : 550]);
                });
            }

            d.wires.forEach((w) => {
                if (result[w.class]) {
                    result[w.class].push([d.ts, w.stagger_mm]);
                }
            });
        }

        return result;
    }, [data, zoomBounds]);

    const option = useMemo(() => {
        if (!(chartData && routeIndexes)) return;

        return {
            legend: {
                show: true,
                top: 10,
            },
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                formatter: (params) => {
                    if (params.length > 0) {
                        const positionString = calculatedPositionString(params[0].axisValue);

                        return `<div class="Tooltip">
                            <div><span><strong>${positionString}</strong></span></div>
                            ${params
                                .map((p) => {
                                    return `<div>
                                        <div>
                                            ${p.marker}
                                            <span>${p.seriesName}</span>
                                        </div>
                                        <span><strong>${Math.round(p.data[1])}</strong>${p.axisId === "acceleration_x" ? "mm/s²" : "mm"}</span>
                                    </div>`;
                                })
                                .join("")}
                        </div>`;
                    }
                },
            },
            axisPointer: [
                {
                    link: [
                        {
                            xAxisId: ["overview_x", "position_x"],
                        },
                    ],
                },
            ],
            grid: [
                { id: "height_grid", top: "5%", left: 80, right: 20, height: "27.5%" },
                { id: "stagger_grid", top: "35%", left: 80, right: 20, height: "27.5%" },
                { id: "acceleration_grid", top: "65%", left: 80, right: 20, height: "27.5%" },
                { id: "position_grid", top: "5%", left: 80, right: 20, bottom: "7.5%" },
                { id: "overview_grid", top: "93.5%", left: 80, right: 20, height: "5.5%" },
            ],
            xAxis: [
                {
                    id: "height_x",
                    gridId: "height_grid",
                    type: "time",
                    min: zoomBounds[0],
                    max: zoomBounds[1],
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                    axisPointer: { label: { show: false }, lineStyle: { opacity: 0 } },
                },
                {
                    id: "stagger_x",
                    gridId: "stagger_grid",
                    type: "time",
                    min: zoomBounds[0],
                    max: zoomBounds[1],
                    axisLine: { onZero: false, offset: { y: -550 }, lineStyle: { opacity: 0 } },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                    axisPointer: { label: { show: false }, lineStyle: { opacity: 0 } },
                },
                {
                    id: "acceleration_x",
                    gridId: "acceleration_grid",
                    type: "time",
                    min: zoomBounds[0],
                    max: zoomBounds[1],
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                    axisPointer: { label: { show: false }, lineStyle: { opacity: 0 } },
                },
                {
                    id: "position_x",
                    gridId: "position_grid",
                    type: "time",
                    min: zoomBounds[0],
                    max: zoomBounds[1],
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                    axisPointer: { label: { show: false } },
                },
                {
                    id: "overview_x",
                    type: "time",
                    min: "dataMin",
                    max: "dataMax",
                    gridId: "overview_grid",
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                    axisPointer: {
                        show: true,
                        snap: false,
                        label: { show: false },
                    },
                    tooltip: { show: false },
                },
            ],
            yAxis: [
                {
                    id: "height_y",
                    gridId: "height_grid",
                    type: "value",
                    name: "Pan Height (mm)",
                    nameLocation: "center",
                    nameTextStyle: { fontSize: 14, fontWeight: "bold" },
                    nameGap: 50,
                    nameRotate: 90,
                    min: data.train_height_mm,
                    max: 6500,
                    axisPointer: { show: false },
                },
                {
                    id: "stagger_y",
                    gridId: "stagger_grid",
                    type: "value",
                    name: "Pan Stagger (mm)",
                    inverse: true,
                    nameLocation: "center",
                    nameTextStyle: { fontSize: 14, fontWeight: "bold" },
                    nameGap: 50,
                    nameRotate: 90,
                    min: -550,
                    max: 550,
                    axisPointer: { show: false },
                },
                {
                    id: "acceleration_y",
                    gridId: "acceleration_grid",
                    type: "value",
                    name: "Acceleration (mm/s²)",
                    nameLocation: "center",
                    nameTextStyle: { fontSize: 14, fontWeight: "bold" },
                    nameGap: 50,
                    nameRotate: 90,
                    min: -12000,
                    max: 12000,
                    axisPointer: { show: false },
                },
                {
                    id: "position_y",
                    show: false,
                    gridId: "position_grid",
                    type: "value",
                    name: "Current Position",
                    min: 0,
                    max: 1,
                    axisPointer: { show: false },
                },
                {
                    id: "overview_y",
                    min: 0,
                    max: 1,
                    gridId: "overview_grid",
                    axisLine: { show: false, onZero: false },
                    axisTick: { show: false },
                    axisLabel: { show: false },
                    splitLine: { show: false },
                    axisPointer: { show: false },
                },
            ],
            dataset: [
                {
                    id: "overview_dataset",
                    source: routeIndexes,
                    dimensions: [
                        { name: "name", type: "ordinal" },
                        { name: "startTimestamp", type: "time" },
                        { name: "endTimestamp", type: "time" },
                        { name: "startPosition", type: "number" },
                        { name: "endPosition", type: "number" },
                    ],
                },
            ],
            series: [
                {
                    type: "line",
                    id: "position_series",
                    xAxisId: "position_x",
                    yAxisId: "position_y",
                    data: chartData.dataPoints,
                    showSymbol: false,
                    lineStyle: { opacity: 0 },
                    tooltip: { show: false },
                    silent: true,
                    animation: false,
                },
                {
                    type: "custom",
                    id: "selected_timestamp_position_series",
                    xAxisId: "position_x",
                    yAxisId: "position_y",
                    data: [],
                    renderItem: function (params, api) {
                        const index = api.value(0);
                        const startPoint = api.coord([index, 1]);
                        const height = api.size([0, 1])[1];

                        const bounds = echarts.graphic.clipRectByRect(
                            {
                                x: startPoint[0],
                                y: startPoint[1],
                                width: 0,
                                height: height,
                            },
                            {
                                x: params.coordSys.x,
                                y: params.coordSys.y,
                                width: params.coordSys.width,
                                height: params.coordSys.height,
                            },
                        );

                        return (
                            bounds && {
                                type: "line",
                                transition: ["shape"],
                                emphasisDisabled: true,
                                shape: {
                                    x1: bounds.x,
                                    x2: bounds.x,
                                    y1: bounds.y,
                                    y2: bounds.y + bounds.height,
                                },
                                style: {
                                    stroke: "red",
                                    lineWidth: 1,
                                },
                            }
                        );
                    },
                    emphasis: {
                        disabled: true,
                    },
                    animation: false,
                    tooltip: { show: false },
                },
                {
                    type: "line",
                    id: "height_mark_area_series",
                    xAxisId: "height_x",
                    yAxisId: "height_y",
                    data: [],
                    markArea: {
                        silent: true,
                        itemStyle: { opacity: 0.3 },
                        data: [
                            [
                                { yAxis: 6500, itemStyle: { color: "red" } },
                                { yAxis: data.pan_max_elevation_mm, itemStyle: { color: "red" } },
                            ],
                            [
                                { yAxis: data.pan_max_elevation_mm, itemStyle: { color: "green", opacity: 0.15 } },
                                { yAxis: data.pan_min_elevation_mm, itemStyle: { color: "green", opacity: 0.15 } },
                            ],
                            [
                                { yAxis: data.pan_min_elevation_mm, itemStyle: { color: "red" } },
                                { yAxis: data.train_height_mm, itemStyle: { color: "red" } },
                            ],
                        ],
                    },
                    showSymbol: false,
                    lineStyle: { opacity: 0 },
                    tooltip: { show: false },
                    silent: true,
                    animation: false,
                },
                {
                    type: "bar",
                    id: "height_pole_event_series",
                    xAxisId: "height_x",
                    yAxisId: "height_y",
                    barWidth: 1,
                    data: chartData.poleEvents,
                    showSymbol: false,
                    itemStyle: {
                        color: "mediumorchid",
                    },
                    tooltip: { show: false },
                    silent: true,
                    animation: false,
                },
                {
                    type: "scatter",
                    id: "height_series",
                    name: "Pan height",
                    xAxisId: "height_x",
                    yAxisId: "height_y",
                    symbolSize: 6,
                    data: chartData.height,
                    itemStyle: {
                        color: "#ff7f0e", // Orange
                    },
                    large: true,
                    animation: false,
                    emphasis: { disabled: true },
                },
                {
                    type: "line",
                    id: "height_smooth_series",
                    name: "Pan height (smoothed)",
                    xAxisId: "height_x",
                    yAxisId: "height_y",
                    symbol: "none",
                    data: chartData.smoothed,
                    itemStyle: {
                        width: 2,
                        color: "brown",
                    },
                    animation: false,
                },
                {
                    type: "line",
                    id: "stagger_mark_area_series",
                    xAxisId: "stagger_x",
                    yAxisId: "stagger_y",
                    data: chartData.dataPoints,
                    markArea: {
                        silent: true,
                        itemStyle: { opacity: 0.15 },
                        data: [
                            [
                                { yAxis: -550, itemStyle: { color: "red" } },
                                { yAxis: -data.red_zone_mm, itemStyle: { color: "red" } },
                            ],
                            [
                                { yAxis: -data.red_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                                { yAxis: -data.green_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                            ],
                            [
                                { yAxis: data.green_zone_mm * -1, itemStyle: { color: "green" } },
                                { yAxis: data.green_zone_mm, itemStyle: { color: "green" } },
                            ],
                            [
                                { yAxis: data.green_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                                { yAxis: data.red_zone_mm, itemStyle: { color: "red", opacity: 0.3 } },
                            ],
                            [
                                { yAxis: data.red_zone_mm, itemStyle: { color: "red" } },
                                { yAxis: 550, itemStyle: { color: "red" } },
                            ],
                        ],
                    },
                    showSymbol: false,
                    lineStyle: { opacity: 0 },
                    tooltip: { show: false },
                    silent: true,
                    animation: false,
                },
                {
                    type: "bar",
                    id: "stagger_pole_event_series",
                    xAxisId: "stagger_x",
                    yAxisId: "stagger_y",
                    barWidth: 1,
                    data: chartData.poleEventsBySide,
                    itemStyle: {
                        color: "mediumorchid",
                    },
                    animation: false,
                    tooltip: { show: false },
                },
                {
                    type: "scatter",
                    id: "stagger_contact_wire_series",
                    name: "Contact wire",
                    xAxisId: "stagger_x",
                    yAxisId: "stagger_y",
                    symbolSize: 3,
                    data: chartData.contact,
                    itemStyle: {
                        color: "#2ca02c", // Green
                    },
                    large: true,
                    animation: false,
                    emphasis: { disabled: true },
                },
                {
                    type: "scatter",
                    id: "stagger_unclassified_wire_series",
                    name: "Unclassified wire",
                    xAxisId: "stagger_x",
                    yAxisId: "stagger_y",
                    symbolSize: 3,
                    data: chartData.unclassified,
                    itemStyle: {
                        color: "#1f77b4", // Blue
                    },
                    large: true,
                    animation: false,
                    emphasis: { disabled: true },
                },
                {
                    type: "scatter",
                    id: "stagger_catenary_wire_series",
                    name: "Catenary wire",
                    xAxisId: "stagger_x",
                    yAxisId: "stagger_y",
                    symbolSize: 3,
                    data: chartData.catenary,
                    itemStyle: {
                        color: "#d62728", // Red
                    },
                    large: true,
                    animation: false,
                    emphasis: { disabled: true },
                },
                {
                    type: "line",
                    id: "acceleration_mark_area_series",
                    xAxisId: "acceleration_x",
                    yAxisId: "acceleration_y",
                    data: [],
                    markArea: {
                        silent: true,
                        itemStyle: { opacity: 0.3 },
                        data: [
                            [
                                { yAxis: 12000, itemStyle: { color: "red" } },
                                { yAxis: 10000, itemStyle: { color: "red" } },
                            ],
                            [
                                { yAxis: 10000, itemStyle: { color: "green", opacity: 0.15 } },
                                { yAxis: -10000, itemStyle: { color: "green", opacity: 0.15 } },
                            ],
                            [
                                { yAxis: -10000, itemStyle: { color: "red" } },
                                { yAxis: -12000, itemStyle: { color: "red" } },
                            ],
                        ],
                    },
                    showSymbol: false,
                    lineStyle: { opacity: 0 },
                    tooltip: { show: false },
                    silent: true,
                    animation: false,
                },
                {
                    type: "bar",
                    id: "acceleration_pole_event_series",
                    xAxisId: "acceleration_x",
                    yAxisId: "acceleration_y",
                    barWidth: 1,
                    data: chartData.poleEvents,
                    showSymbol: false,
                    itemStyle: {
                        color: "mediumorchid",
                    },
                    tooltip: { show: false },
                    silent: true,
                    animation: false,
                },
                {
                    type: "line",
                    id: "acceleration_series",
                    name: "Vertical acceleration",
                    xAxisId: "acceleration_x",
                    yAxisId: "acceleration_y",
                    symbol: "none",
                    data: chartData.acceleration,
                    itemStyle: {
                        color: "#ff7f0e", // Orange
                    },
                    animation: false,
                },
                {
                    type: "custom",
                    id: "overview_series",
                    datasetId: "overview_dataset",
                    xAxisId: "overview_x",
                    yAxisId: "overview_y",
                    encode: {
                        x: ["startTimestamp", "endTimestamp"],
                        value: ["name", "startTimestamp", "endTimestamp", "startPosition", "endPosition"],
                    },
                    renderItem: (params, api) => {
                        const routeName = api.value(0);
                        const startTimestamp = api.value(1);
                        const endTimestamp = api.value(2);
                        const startPosition = api.value(3);
                        const endPosition = api.value(4);

                        const startMile = Math.floor(startPosition / 80);
                        const endMile = Math.floor(endPosition / 80);

                        const startChain = Math.floor((startPosition - startMile * 80) / 10);
                        const endChain = Math.floor((endPosition - endMile * 80) / 10);

                        const startPoint = api.coord([startTimestamp, 0.75]);
                        const endPoint = api.coord([endTimestamp, 0.25]);

                        const bounds = echarts.graphic.clipRectByRect(
                            {
                                x: startPoint[0],
                                y: startPoint[1],
                                width: endPoint[0] - startPoint[0],
                                height: endPoint[1] - startPoint[1],
                            },
                            {
                                x: params.coordSys.x,
                                y: params.coordSys.y,
                                width: params.coordSys.width,
                                height: params.coordSys.height,
                            },
                        );

                        if (!bounds) {
                            return null;
                        }

                        const fullElrText = `${routeName} ${startMile}.${startChain} - ${endMile}.${endChain}`;
                        const elrText = `${routeName}`;
                        const fullTextWidth = echarts.format.getTextRect(fullElrText).width;
                        const smallTextWidth = echarts.format.getTextRect(elrText).width;

                        let text = "";
                        if (bounds.width > fullTextWidth + 4) {
                            text = fullElrText;
                        } else if (bounds.width > smallTextWidth + 4) {
                            text = elrText;
                        }

                        return {
                            type: "group",
                            transition: ["shape"],
                            children: [
                                {
                                    type: "line",
                                    transition: ["shape"],
                                    ignore: !bounds,
                                    shape: {
                                        x1: bounds.x,
                                        y1: bounds.y,
                                        x2: bounds.x,
                                        y2: bounds.y + bounds.height,
                                    },
                                    emphasis: { disabled: true },
                                    style: {
                                        stroke: "#000",
                                        lineWidth: 1,
                                    },
                                },
                                {
                                    type: "rect",
                                    transition: ["shape"],
                                    ignore: !bounds,
                                    shape: bounds,
                                    emphasis: { disabled: true },
                                    style: {
                                        text,
                                        fill: "rgba(255, 255, 255, 0.5)",
                                        textFill: "#000",
                                    },
                                },
                            ],
                        };
                    },
                    emphasis: { disabled: true },
                    animation: false,
                    tooltip: { show: false },
                },
                {
                    type: "custom",
                    id: `current_view_series`,
                    xAxisId: "overview_x",
                    yAxisId: "overview_y",
                    data: [zoomBounds],
                    renderItem: function (params, api) {
                        const startIndex = api.value(0);
                        const endIndex = api.value(1);

                        const startPoint = api.coord([startIndex, 0.85]);
                        const endPoint = api.coord([endIndex, 0.15]);

                        const bounds = echarts.graphic.clipRectByRect(
                            {
                                x: startPoint[0],
                                y: startPoint[1],
                                width: endPoint[0] - startPoint[0],
                                height: endPoint[1] - startPoint[1],
                            },
                            {
                                x: params.coordSys.x,
                                y: params.coordSys.y,
                                width: params.coordSys.width,
                                height: params.coordSys.height,
                            },
                        );

                        return (
                            bounds && {
                                type: "rect",
                                ignore: !bounds,
                                transition: ["shape"],
                                shape: bounds,
                                emphasis: { disabled: true },
                                style: {
                                    fill: "rgba(220,192,255,0.5)",
                                    stroke: "rgba(220,192,255,0.9)",
                                },
                            }
                        );
                    },
                    emphasis: { disabled: true },
                    animation: false,
                    tooltip: { show: false },
                },
                {
                    type: "custom",
                    id: "selected_timestamp_overview_series",
                    xAxisId: "overview_x",
                    yAxisId: "overview_y",
                    data: [],
                    renderItem: function (params, api) {
                        const index = api.value(0);

                        const startPoint = api.coord([index, 0.9]);
                        const endPoint = api.coord([index, 0.1]);

                        const bounds = echarts.graphic.clipRectByRect(
                            {
                                x: startPoint[0],
                                y: startPoint[1],
                                width: 0,
                                height: endPoint[1] - startPoint[1],
                            },
                            {
                                x: params.coordSys.x,
                                y: params.coordSys.y,
                                width: params.coordSys.width,
                                height: params.coordSys.height,
                            },
                        );

                        return (
                            bounds && {
                                type: "line",
                                transition: ["shape"],
                                emphasisDisabled: true,
                                shape: {
                                    x1: bounds.x,
                                    x2: bounds.x,
                                    y1: bounds.y,
                                    y2: bounds.y + bounds.height,
                                },
                                style: {
                                    stroke: "red",
                                    lineWidth: 1,
                                },
                            }
                        );
                    },
                    emphasis: {
                        disabled: true,
                    },
                    animation: false,
                    tooltip: { show: false },
                },
            ],
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData, routeIndexes, zoomBounds]);

    useEffect(() => {
        if (chart && option) {
            chart.setOption(option);
            updateCurrentPosition(currentTimestamp, zoomBounds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, option]);

    const onSelectTimestamp = useCallback(
        (timestamp) => {
            setSelectedTimestamp(timestamp);
            dispatch(websocketMessage({ action: "request_position", timestamp: timestamp }));
        },
        [dispatch],
    );

    const onPositionGridClick = useCallback(
        (item) => {
            const timestamp = item.position_grid[0];
            const index = binarySearch(timestamp, data.analysis_results, (geom) => geom.ts);
            onSelectTimestamp(data.analysis_results[index].ts);
        },
        [data, onSelectTimestamp],
    );

    useOnClick(chart, ["position_grid"], onPositionGridClick);

    const selectBounds = useCallback(
        (bounds) => {
            setZoomBounds(bounds);
            viewBounds(bounds);
        },
        [viewBounds],
    );

    const goToTimestamp = useCallback(
        (timestamp) => {
            onSelectTimestamp(timestamp);
            const halfSpan = (zoomBounds[1] - zoomBounds[0]) / 2;
            const newBounds = boundedRange(maxBoundsRef.current[0], timestamp - halfSpan, timestamp + halfSpan, maxBoundsRef.current[1]);
            selectBounds(newBounds);
        },
        [onSelectTimestamp, selectBounds, zoomBounds],
    );

    const dragCallback = useCallback(
        (dragState) => {
            if (dragState.type !== "drag") return;

            const coordinates = dragState.current;
            const timestamp = coordinates[0];
            const newBounds = boundedRange(maxBoundsRef.current[0], timestamp - MAX_ZOOM / 2, timestamp + MAX_ZOOM / 2, maxBoundsRef.current[1]);
            viewBounds(newBounds);
        },
        [viewBounds],
    );

    const endDragCallback = useCallback(
        (dragState) => {
            if (dragState.type !== "drag") return;

            const coordinates = dragState.current;
            goToTimestamp(coordinates[0]);
        },
        [goToTimestamp],
    );

    useMouseMovement(chart, "overview_grid", dragCallback, endDragCallback);

    const calculateNewBounds = (dragState, zoomBounds, maxBounds) => {
        const [currentMin, currentMax] = zoomBounds;
        const [minBound, maxBound] = maxBounds;

        if (dragState.delta[2]) {
            // Zoom
            const timestamp = dragState.start[0];
            const range = currentMax - currentMin;

            let newRange = range * Math.pow(4, dragState.delta[2]);

            // Clamp range
            newRange = boundedValue(MIN_ZOOM, newRange, MAX_ZOOM);

            const ratio = (timestamp - currentMin) / range;

            let newStart = timestamp - ratio * newRange;
            let newEnd = newStart + newRange;

            if (newStart < minBound) {
                newStart = minBound;
                newEnd = newStart + newRange;
            }
            if (newEnd > maxBound) {
                newEnd = maxBound;
                newStart = maxBound - newRange;
            }

            return [newStart, newEnd];
        } else {
            // Pan
            const timestampDelta = Math.round(dragState.delta[0]);

            let newStart = currentMin + timestampDelta;
            let newEnd = currentMax + timestampDelta;

            if (newStart < minBound) {
                newStart = minBound;
                newEnd = newStart + (currentMax - currentMin);
            }

            if (newEnd > maxBound) {
                newEnd = maxBound;
                newStart = maxBound - (currentMax - currentMin);
            }

            return [newStart, newEnd];
        }
    };

    const boundedZoomRange = (min, low, high, max) => {
        let newLow = Math.max(min, low);
        let newHigh = Math.min(max, high);

        let range = newHigh - newLow;

        if (range < MIN_ZOOM) {
            // Expand to minimum range
            const center = newLow + range / 2;
            newLow = Math.max(min, center - MIN_ZOOM / 2);
            newHigh = Math.min(max, center + MIN_ZOOM / 2);
        } else if (range > MAX_ZOOM) {
            // Reduce to maximum range
            const center = newLow + range / 2;
            newLow = Math.max(min, center - MAX_ZOOM / 2);
            newHigh = Math.min(max, center + MAX_ZOOM / 2);
        }

        return [newLow, newHigh];
    };

    const positionDragCallback = useCallback(
        (dragState) => {
            if (chart) {
                const newBounds = calculateNewBounds(dragState, zoomBounds, maxBoundsRef.current);

                viewBounds(newBounds);
            }
        },
        [chart, viewBounds, zoomBounds],
    );

    const positionEndDragCallback = useCallback(
        (dragState) => {
            if (chart) {
                let newBounds = calculateNewBounds(dragState, zoomBounds, maxBoundsRef.current);
                newBounds = boundedZoomRange(maxBoundsRef.current[0], newBounds[0], newBounds[1], maxBoundsRef.current[1]);

                selectBounds(newBounds);
            }
        },
        [chart, selectBounds, zoomBounds],
    );

    useMouseMovement(chart, "position_grid", positionDragCallback, positionEndDragCallback);

    if (!initialised) {
        return <LoadingOverlay loading={true} />;
    }

    if (!hasTraceData) {
        return (
            <div className="widget__Info center">
                <p>No data for this session.</p>
            </div>
        );
    }

    return (
        <div className="PantovisionContainer">
            <Measure
                bounds
                onResize={onResize}>
                {({ measureRef }) => (
                    <div
                        className="PantovisionInner"
                        ref={measureRef}>
                        <div
                            className="Pantovision"
                            ref={chartRef}></div>
                    </div>
                )}
            </Measure>
        </div>
    );
};

export default Pantovision;
